import { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../components/ui/card';
import {
  Bot, Play, Square, RefreshCw, TrendingUp, Zap, Layers,
  AlertCircle, Wallet, ClipboardPaste, CheckCircle, XCircle,
  Shield, Coins, AlertTriangle, BarChart2, Activity, Clock,
  ChevronDown, ChevronUp, ExternalLink, Info, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import TokenSafetyCheck from '../components/TokenSafetyCheck';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHAIN_MAP = {
  pump_fun:    'solana',
  raydium:     'solana',
  jupiter:     'solana',
  uniswap:     'evm',
  pancakeswap: 'bsc',
};

function isValidAddress(address, chain) {
  if (!address || address.length < 20) return false;
  if (chain === 'solana') return /^[1-9A-HJ-NP-Za-km-z]{32,50}$/.test(address);
  return /^0x[0-9a-fA-F]{40,}$/.test(address);
}

const fmt    = (n, d = 2) => (parseFloat(n) || 0).toFixed(d);
const pnlCls = (n) => parseFloat(n) > 0 ? 'text-green-400' : parseFloat(n) < 0 ? 'text-red-400' : 'text-slate-400';

// ── shared UI atoms ────────────────────────────────────────────────────────
function StatPill({ label, value, accent }) {
  return (
    <div className="flex flex-col items-center px-3 py-2 rounded-lg bg-slate-700/50 min-w-[72px]">
      <span className={`text-sm font-bold font-mono ${accent || 'text-white'}`}>{value}</span>
      <span className="text-[10px] text-slate-400 mt-0.5 text-center leading-tight">{label}</span>
    </div>
  );
}

function BotStatusBadge({ status }) {
  const map = {
    running: 'bg-green-600/20 text-green-300 border-green-600/30',
    stopped: 'bg-slate-600/20 text-slate-400 border-slate-600/30',
    error:   'bg-red-600/20   text-red-300   border-red-600/30',
  };
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border capitalize ${map[status] || map.stopped}`}>
      {status === 'running' && (
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 mr-1 animate-pulse" />
      )}
      {status || 'stopped'}
    </span>
  );
}

function SellBlockedBadge({ reason }) {
  if (!reason) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-yellow-900/40 text-yellow-300 border border-yellow-700/30">
      <Shield className="w-2.5 h-2.5" /> {reason}
    </span>
  );
}

function TierBadge({ tier, credits }) {
  const map = {
    STRATEGY: 'bg-slate-700/60 text-slate-300 border-slate-600',
    HARMONY:  'bg-blue-900/40  text-blue-300  border-blue-700/40',
    POWER:    'bg-purple-900/40 text-purple-300 border-purple-700/40',
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${map[tier] || map.STRATEGY}`}>
        {tier || 'STRATEGY'}
      </span>
      <span className={`text-[10px] font-mono ${credits < 5 ? 'text-red-400' : 'text-slate-400'}`}>
        <Coins className="inline w-2.5 h-2.5 mr-0.5" />
        {credits ?? '–'} credits
      </span>
    </div>
  );
}

// ── main component ─────────────────────────────────────────────────────────
export default function VolumeBot() {
  const { panicMode } = useOutletContext();
  const { isSubUser }  = useAuth();

  const [bots, setBots]                     = useState([]);
  const [loading, setLoading]               = useState(false);
  const [exchanges, setExchanges]           = useState([]);
  const [selectedExchange, setSelectedExchange] = useState('');
  const [tokens, setTokens]                 = useState([]);
  const [selectedToken, setSelectedToken]   = useState(null);
  const [pasteAddress, setPasteAddress]     = useState('');
  const [walletStatus, setWalletStatus]     = useState(null);
  const [tierConfig, setTierConfig]         = useState(null);
  const [slippage, setSlippage]             = useState(50);
  const [expandedBot, setExpandedBot]       = useState(null);
  const [safetyBlocked, setSafetyBlocked]   = useState(false);

  const [config, setConfig] = useState({
    min_amount:           10,
    max_amount:           100,
    interval_seconds:     30,
    enabled:              true,
    paper_investment_sol: 0.1,
    paper_mode:           false,
    take_profit_x:        1.005,
    min_profit_pct:       0.5,
  });

  const intervalRef = useRef(null);

  // ── fetchers ───────────────────────────────────────────────────────────
  const fetchBots = useCallback(async () => {
    try {
      if (isSubUser) {
        const { data } = await axios.get(`${API_URL}/api/subuser/bots`);
        const normalized = (data.bots || [])
          .filter(b => b.bot_type === 'VOLUME')
          .map(b => ({
            ...b,
            id:              b.bot_id,
            status:          b.status?.toLowerCase(),
            trades_executed: b.total_trades      || 0,
            total_volume:    b.total_volume_usd  || 0,
            profit:          b.total_earnings_usd || 0,
          }));
        setBots(normalized);
      } else {
        const { data } = await axios.get(`${API_URL}/api/bot/volume/status`);
        setBots(data.bots || []);
      }
    } catch (err) {
      console.error('Failed to fetch volume bots:', err);
    }
  }, [isSubUser]);

  const fetchExchanges = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/defi/exchanges`);
      setExchanges(data.exchanges || []);
    } catch (err) { console.error(err); }
  }, []);

  const fetchWalletStatus = useCallback(async () => {
    try {
      const [walletsRes, connectionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/wallet/connected`),
        axios.get(`${API_URL}/api/exchange/connections`),
      ]);
      setWalletStatus({
        wallets:     walletsRes.data.wallets || [],
        connections: connectionsRes.data.connections || [],
      });
    } catch (err) { console.error(err); }
  }, []);

  const fetchTierConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/credits/tier-config`);
      setTierConfig(data);
      setSlippage(Math.min(50, data.execution_config?.max_slippage_bps || 50));
    } catch (err) { console.error(err); }
  }, []);

  // ── init + polling ─────────────────────────────────────────────────────
  useEffect(() => {
    fetchBots();
    fetchExchanges();
    fetchWalletStatus();
    fetchTierConfig();
    intervalRef.current = setInterval(fetchBots, 3000);
    return () => clearInterval(intervalRef.current);
  }, [fetchBots, fetchExchanges, fetchWalletStatus, fetchTierConfig]);

  // ── exchange change ────────────────────────────────────────────────────
  const handleExchangeChange = async (exchangeId) => {
    setSelectedExchange(exchangeId);
    setSelectedToken(null);
    setPasteAddress('');
    if (!exchangeId) { setTokens([]); return; }
    try {
      const { data } = await axios.get(`${API_URL}/api/defi/exchanges/${exchangeId}/tokens`);
      setTokens(data.tokens || []);
    } catch { toast.error('Failed to fetch tokens'); }
  };

  // ── paste/validate address ─────────────────────────────────────────────
  const handlePasteAddress = () => {
    if (!selectedExchange) { toast.error('Select an exchange first'); return; }
    const chain = CHAIN_MAP[selectedExchange] || 'solana';
    if (!isValidAddress(pasteAddress, chain)) {
      toast.error(`Invalid ${chain === 'solana' ? 'Solana' : 'EVM'} contract address`);
      return;
    }
    const known = tokens.find(t => t.address === pasteAddress);
    if (known) {
      setSelectedToken(known);
      toast.success(`Matched known token: ${known.symbol}`);
    } else {
      setSelectedToken({ symbol: `CUSTOM-${pasteAddress.slice(0, 6)}`, address: pasteAddress, price: 0, change_24h: 0 });
      toast.success('Custom token address set');
    }
  };

  // ── wallet resolver ────────────────────────────────────────────────────
  const getWalletForExchange = () => {
    if (!selectedExchange || !walletStatus) return null;
    const chain        = CHAIN_MAP[selectedExchange];
    const expectedType = chain === 'solana' ? 'phantom' : 'metamask';
    const conn = walletStatus.connections.find(c => c.exchange_id === selectedExchange);
    if (conn) return { address: conn.wallet_address, type: conn.wallet_type || expectedType };
    const w = walletStatus.wallets.find(w => w.chain === chain || w.wallet_type === expectedType);
    if (w) return { address: w.address, type: w.wallet_type || expectedType };
    return null;
  };
  const walletForExchange = getWalletForExchange();

  // ── derived flags ──────────────────────────────────────────────────────
  const lowCredits = tierConfig && tierConfig.credits < 1;
  const execMode   = tierConfig?.execution_config?.execution_mode || 'paper';
  const rpcType    = tierConfig?.execution_config?.rpc_type || 'standard';

  // ── start bot ──────────────────────────────────────────────────────────
  const handleStartBot = async () => {
    if (panicMode)                           { toast.error('Cannot start bots in panic mode'); return; }
    if (!selectedToken || !selectedExchange) { toast.error('Select a DeFi exchange and token pair first'); return; }
    if (!walletForExchange)                  { toast.error('Connect your wallet on the Exchange Connect page first'); return; }
    if (lowCredits)                          { toast.error('Insufficient credits — top up to start bots'); return; }

    setLoading(true);
    try {
      if (isSubUser) {
        const { data } = await axios.post(`${API_URL}/api/subuser/bots`, {
          bot_type:      'VOLUME',
          exchange_id:   selectedExchange,
          token_symbol:  selectedToken.symbol,
          token_address: selectedToken.address,
          config: {
            min_amount:          config.min_amount,
            max_amount:          config.max_amount,
            interval_seconds:    config.interval_seconds,
            paper_investment_sol: config.paper_investment_sol,
            take_profit_x:       config.take_profit_x,
            min_profit_pct:      config.min_profit_pct,
          },
        });
        toast.success(data.message);
      } else {
        const { data } = await axios.post(`${API_URL}/api/bot/volume/start`, {
          ...config,
          token_address: selectedToken.address,
          token_symbol:  selectedToken.symbol,
          exchange_id:   selectedExchange,
          exchange_name: exchanges.find(e => e.id === selectedExchange)?.name || '',
        });
        toast.success(data.message);
      }
      fetchBots();
      fetchTierConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start bot');
    } finally {
      setLoading(false);
    }
  };

  // ── stop / restart ─────────────────────────────────────────────────────
  const handleStopBot = async (botId) => {
    try {
      if (isSubUser) await axios.post(`${API_URL}/api/subuser/bots/${botId}/stop`);
      else           await axios.post(`${API_URL}/api/bot/volume/stop/${botId}`);
      toast.success('Bot stopped');
      fetchBots();
    } catch { toast.error('Failed to stop bot'); }
  };

  const handleRestartBot = async (botId) => {
    try {
      await axios.post(`${API_URL}/api/bot/volume/restart/${botId}`);
      toast.success('Bot restarted with saved config!');
      fetchBots();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to restart bot'); }
  };

  // ── aggregated stats ───────────────────────────────────────────────────
  const activeBots  = bots.filter(b => b.status === 'running').length;
  const totalVolume = bots.reduce((s, b) => s + (b.total_volume    || 0), 0);
  const totalTrades = bots.reduce((s, b) => s + (b.trades_executed || 0), 0);
  const totalProfit = bots.reduce((s, b) => s + (b.profit          || 0), 0);

  // ── render ─────────────────────────────────────────────────────────────
  return (
    <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto">

      {/* ── page header ── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Bot className="w-5 h-5 text-blue-400" />
            VolumeBot
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Automated buy/sell cycles · Profit gate enforced · Sells always to SOL
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {tierConfig && (
            <TierBadge tier={tierConfig.tier} credits={tierConfig.credits} />
          )}
          {panicMode && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-900/30 border border-red-700/40 text-red-300 text-xs font-semibold">
              <AlertCircle className="w-3.5 h-3.5" /> PANIC MODE — halted
            </div>
          )}
        </div>
      </div>

      {/* ── low credits warning ── */}
      {lowCredits && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-900/20 border border-red-700/30 text-red-300 text-xs">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>Credits exhausted — top up to run Live bots. Paper mode continues.</span>
        </div>
      )}

      {/* ── summary stats bar ── */}
      {bots.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <StatPill label="Active"       value={activeBots}                         accent={activeBots > 0 ? 'text-green-400' : 'text-slate-400'} />
          <StatPill label="Total Bots"   value={bots.length} />
          <StatPill label="Trades"       value={totalTrades} />
          <StatPill label="Volume"       value={`$${fmt(totalVolume)}`} />
          <StatPill label="Profit"       value={`${totalProfit >= 0 ? '+' : ''}$${fmt(totalProfit)}`} accent={pnlCls(totalProfit)} />
          <StatPill label="Slippage"     value={`${slippage} bps`} />
          <div className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-[10px] font-semibold ${
            execMode === 'mainnet' || execMode === 'live'
              ? 'bg-green-900/30 border-green-700/30 text-green-300'
              : 'bg-slate-700/40 border-slate-600 text-slate-400'
          }`}>
            <Activity className="w-3 h-3" />
            {execMode === 'mainnet' || execMode === 'live' ? 'Live' : 'Paper'}
            {rpcType && <span className="ml-1 opacity-60">· {rpcType}</span>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ══════════════════════════════════════════════
            LEFT — config panel
        ══════════════════════════════════════════════ */}
        <div className="lg:col-span-1 space-y-4">

          {/* token selection */}
          <Card className="bg-slate-800/60 border-slate-700">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                <Coins className="w-4 h-4 text-yellow-400" /> Token Selection
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">

              {/* exchange */}
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Exchange</Label>
                <select
                  value={selectedExchange}
                  onChange={e => handleExchangeChange(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-md px-3 h-9 focus:outline-none focus:border-blue-500"
                >
                  <option value="">Select exchange…</option>
                  {exchanges.map(ex => (
                    <option key={ex.id} value={ex.id}>{ex.name}</option>
                  ))}
                </select>
              </div>

              {/* known token list */}
              {tokens.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">Token</Label>
                  <select
                    value={selectedToken?.address || ''}
                    onChange={e => setSelectedToken(tokens.find(t => t.address === e.target.value) || null)}
                    className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-md px-3 h-9 focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Select token…</option>
                    {tokens.map(t => (
                      <option key={t.address} value={t.address}>{t.symbol} — {t.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* paste address */}
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Or paste contract address</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Token address…"
                    value={pasteAddress}
                    onChange={e => setPasteAddress(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white h-9 text-sm font-mono flex-1"
                  />
                  <Button
                    size="sm" variant="outline"
                    onClick={handlePasteAddress}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700 h-9 px-2 shrink-0"
                  >
                    <ClipboardPaste className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* selected token preview */}
              {selectedToken && (
                <div className="flex items-center justify-between p-2 rounded-lg bg-blue-900/20 border border-blue-700/30">
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-blue-300">{selectedToken.symbol}</p>
                    <p className="text-[10px] text-slate-400 font-mono truncate">{selectedToken.address}</p>
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    {parseFloat(selectedToken.price) > 0 && (
                      <p className="text-[10px] font-mono text-slate-300">${parseFloat(selectedToken.price).toFixed(6)}</p>
                    )}
                    {selectedToken.change_24h !== undefined && (
                      <p className={`text-[10px] ${parseFloat(selectedToken.change_24h) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {parseFloat(selectedToken.change_24h) >= 0 ? '+' : ''}{fmt(selectedToken.change_24h)}%
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Trust Layer — pre-trade safety check */}
              {selectedToken?.address && (
                <TokenSafetyCheck
                  mint={selectedToken.address}
                  source="commander-volume-bot"
                  onGate={setSafetyBlocked}
                />
              )}

              {/* wallet pill */}
              {selectedExchange && (
                <div className={`flex items-center gap-2 p-2 rounded-lg border text-xs ${
                  walletForExchange
                    ? 'bg-green-900/20 border-green-700/30 text-green-300'
                    : 'bg-yellow-900/20 border-yellow-700/30 text-yellow-300'
                }`}>
                  <Wallet className="w-3.5 h-3.5 shrink-0" />
                  {walletForExchange
                    ? `${walletForExchange.address.slice(0, 8)}…${walletForExchange.address.slice(-6)}`
                    : 'No wallet connected — go to Exchange Connect'}
                </div>
              )}
            </CardContent>
          </Card>

          {/* bot config */}
          <Card className="bg-slate-800/60 border-slate-700">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                <Layers className="w-4 h-4 text-slate-400" /> Bot Config
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">Min Amount (USD)</Label>
                  <Input
                    type="number"
                    value={config.min_amount}
                    onChange={e => setConfig(p => ({ ...p, min_amount: parseFloat(e.target.value) || 10 }))}
                    className="bg-slate-700 border-slate-600 text-white h-9 text-sm"
                    min="1"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">Max Amount (USD)</Label>
                  <Input
                    type="number"
                    value={config.max_amount}
                    onChange={e => setConfig(p => ({ ...p, max_amount: parseFloat(e.target.value) || 100 }))}
                    className="bg-slate-700 border-slate-600 text-white h-9 text-sm"
                    min="1"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">Interval (s)</Label>
                  <Input
                    type="number"
                    value={config.interval_seconds}
                    onChange={e => setConfig(p => ({ ...p, interval_seconds: parseInt(e.target.value) || 30 }))}
                    className="bg-slate-700 border-slate-600 text-white h-9 text-sm"
                    min="10"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">Paper SOL</Label>
                  <Input
                    type="number"
                    value={config.paper_investment_sol}
                    onChange={e => setConfig(p => ({ ...p, paper_investment_sol: parseFloat(e.target.value) || 0.1 }))}
                    className="bg-slate-700 border-slate-600 text-white h-9 text-sm"
                    min="0.01"
                    step="0.01"
                  />
                </div>
              </div>

              {/* ── Take Profit Settings ── */}
              <div className="p-2 rounded-lg bg-slate-700/30 border border-slate-600/30 space-y-2">
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide">Take Profit Settings</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">Take Profit (×)</Label>
                    <Input
                      type="number"
                      value={config.take_profit_x}
                      onChange={e => setConfig(p => ({ ...p, take_profit_x: parseFloat(e.target.value) || 1.005 }))}
                      className="bg-slate-700 border-slate-600 text-white h-9 text-sm"
                      min="1.001"
                      step="0.001"
                    />
                    <p className="text-[9px] text-slate-500">e.g. 1.5 = sell at 50% gain</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">Min Profit %</Label>
                    <Input
                      type="number"
                      value={config.min_profit_pct}
                      onChange={e => setConfig(p => ({ ...p, min_profit_pct: parseFloat(e.target.value) || 0.5 }))}
                      className="bg-slate-700 border-slate-600 text-white h-9 text-sm"
                      min="0.1"
                      step="0.1"
                    />
                    <p className="text-[9px] text-slate-500">Never sells at a loss</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between py-1">
                <Label className="text-xs text-slate-400">Enabled</Label>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={v => setConfig(p => ({ ...p, enabled: v }))}
                />
              </div>

              <div className={`flex items-center justify-between py-1 px-2 rounded-lg border ${
                config.paper_mode
                  ? 'bg-yellow-900/20 border-yellow-700/30'
                  : 'bg-green-900/20 border-green-700/30'
              }`}>
                <div className="flex flex-col">
                  <Label className={`text-xs font-semibold ${config.paper_mode ? 'text-yellow-300' : 'text-green-300'}`}>
                    {config.paper_mode ? '📄 Paper Mode' : '🟢 Live Mode'}
                  </Label>
                  <span className="text-[10px] text-slate-500 mt-0.5">
                    {config.paper_mode ? 'Simulates trades — no SOL spent' : 'Executes real swaps on-chain'}
                  </span>
                </div>
                <Switch
                  checked={config.paper_mode}
                  onCheckedChange={v => setConfig(p => ({ ...p, paper_mode: v }))}
                />
              </div>

              {/* slippage display (read-only from tier) */}
              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-700/40 border border-slate-600/40">
                <span className="text-xs text-slate-400 flex items-center gap-1">
                  <Zap className="w-3 h-3" /> Slippage
                </span>
                <span className="text-xs font-mono text-slate-300">{slippage} bps</span>
              </div>

              {/* safety note */}
              <div className="p-2 rounded-lg bg-blue-900/20 border border-blue-700/30 flex items-start gap-2">
                <Info className="w-3.5 h-3.5 text-blue-400 mt-0.5 shrink-0" />
                <p className="text-[10px] text-blue-300 leading-relaxed">
                  Sells always route to <strong>SOL</strong>. Profit gate requires &gt;0.5% net gain before any sell fires.
                  On restart, orphaned tokens are sold first.
                </p>
              </div>

              <Button
                className="w-full bg-blue-700 hover:bg-blue-600 text-white h-9 font-semibold text-sm"
                onClick={handleStartBot}
                disabled={loading || panicMode || !selectedToken || !walletForExchange || lowCredits || safetyBlocked}
              >
                {loading
                  ? <><RefreshCw className="w-3.5 h-3.5 mr-2 animate-spin" /> Starting…</>
                  : <><Play className="w-3.5 h-3.5 mr-2" /> Start VolumeBot</>
                }
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* ══════════════════════════════════════════════
            RIGHT — running bots list
        ══════════════════════════════════════════════ */}
        <div className="lg:col-span-2 space-y-3">

          {/* empty state */}
          {bots.length === 0 && (
            <Card className="bg-slate-800/60 border-slate-700">
              <CardContent className="py-14 flex flex-col items-center text-center gap-3">
                <Bot className="w-12 h-12 text-slate-600" />
                <p className="text-slate-400 font-medium">No VolumeBots running</p>
                <p className="text-sm text-slate-500 max-w-xs">
                  Select an exchange and token, set your amount range, and hit <strong>Start VolumeBot</strong>.
                </p>
              </CardContent>
            </Card>
          )}

          {/* bot cards */}
          {bots.map((bot) => {
            const isRunning  = bot.status === 'running';
            const isExpanded = expandedBot === (bot.id || bot.bot_id);
            const botId      = bot.id || bot.bot_id;
            const execModeBot    = bot.execution_mode || 'paper';
            const profit         = parseFloat(bot.profit || 0);
            const paperPnl       = parseFloat(bot.paper_pnl || 0);
            const isPaperBot     = execModeBot === 'paper' || bot.config?.paper_mode;
            const displayPnl     = isPaperBot ? paperPnl : profit;
            const tokensHeld     = parseInt(bot.tokens_held_vol || bot.tokens_held || 0);
            const sellEveryN     = bot.sell_every_n || bot.config?.sell_every_n_buys;

            return (
              <Card
                key={botId}
                className={`border transition-colors ${
                  isRunning ? 'bg-slate-800/70 border-blue-700/30' : 'bg-slate-800/40 border-slate-700'
                }`}
              >
                <CardContent className="p-4">

                  {/* ── header row ── */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-white">{bot.token_symbol || 'Unknown'}</span>
                        <BotStatusBadge status={bot.status} />
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                          execModeBot === 'mainnet' || execModeBot === 'live'
                            ? 'bg-green-900/30 border-green-700/30 text-green-300'
                            : 'bg-slate-700/50 border-slate-600 text-slate-400'
                        }`}>
                          {isPaperBot ? '📄 Paper' : '🟢 Live'}
                        </span>
                      </div>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {bot.exchange_name || bot.exchange_id} · {botId}
                      </p>
                      {bot.last_sell_blocked && (
                        <div className="mt-1">
                          <SellBlockedBadge reason={bot.last_sell_blocked} />
                        </div>
                      )}
                      {bot.last_error && (
                        <p className="text-[10px] text-red-400 mt-1 truncate">⚠ {bot.last_error}</p>
                      )}
                    </div>

                    {/* profit + volume */}
                    <div className="text-right shrink-0">
                      <p className={`text-sm font-bold font-mono ${pnlCls(displayPnl)}`}>
                        {displayPnl >= 0 ? '+' : ''}${fmt(displayPnl)}
                      </p>
                      <p className="text-[10px] text-slate-500">{isPaperBot ? 'paper pnl' : 'profit'}</p>
                      {bot.total_volume > 0 && (
                        <>
                          <p className="text-xs font-mono text-slate-300 mt-0.5">${fmt(bot.total_volume)}</p>
                          <p className="text-[10px] text-slate-500">volume</p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* ── metric pills ── */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <div className="flex items-center gap-1 text-[10px] text-slate-400">
                      <BarChart2 className="w-3 h-3" />
                      {bot.trades_executed || 0} trades
                    </div>
                    {tokensHeld > 0 && (
                      <div className="flex items-center gap-1 text-[10px] text-yellow-300">
                        <Layers className="w-3 h-3" />
                        {tokensHeld.toLocaleString()} tokens held
                      </div>
                    )}
                    {sellEveryN && (
                      <div className="flex items-center gap-1 text-[10px] text-slate-400">
                        <Clock className="w-3 h-3" />
                        Sell every {sellEveryN} buys
                      </div>
                    )}
                    {bot.config?.min_amount && (
                      <div className="flex items-center gap-1 text-[10px] text-slate-400">
                        <Zap className="w-3 h-3" />
                        ${bot.config.min_amount}–${bot.config.max_amount} / {bot.config.interval_seconds}s
                      </div>
                    )}
                  </div>

                  {/* ── action buttons ── */}
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    {isRunning ? (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => handleStopBot(botId)}
                        className="border-red-700/50 text-red-300 hover:bg-red-900/30 h-7 px-3 text-xs"
                      >
                        <Square className="w-3 h-3 mr-1" /> Stop
                      </Button>
                    ) : (
                      !isSubUser && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => handleRestartBot(botId)}
                          className="border-blue-700/50 text-blue-300 hover:bg-blue-900/30 h-7 px-3 text-xs"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" /> Restart
                        </Button>
                      )
                    )}
                    <button
                      onClick={() => setExpandedBot(isExpanded ? null : botId)}
                      className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-300 ml-auto"
                    >
                      <Eye className="w-3 h-3" />
                      {isExpanded ? 'Hide' : 'Logs'}
                      {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                  </div>

                  {/* ── expanded swap log ── */}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-slate-700">
                      <p className="text-[10px] text-slate-500 mb-2">Recent swap log (last 10)</p>
                      {(bot.swap_logs || []).length === 0 ? (
                        <p className="text-[10px] text-slate-600 italic">No swaps recorded yet</p>
                      ) : (
                        <div className="space-y-1 max-h-44 overflow-y-auto pr-1">
                          {[...(bot.swap_logs || [])].reverse().slice(0, 10).map((log, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between text-[10px] py-1 border-b border-slate-700/40 last:border-0"
                            >
                              <div className="flex items-center gap-1.5 min-w-0">
                                {log.success
                                  ? <CheckCircle className="w-3 h-3 text-green-400 shrink-0" />
                                  : <XCircle    className="w-3 h-3 text-red-400   shrink-0" />
                                }
                                <span className="capitalize text-slate-400">{(log.type || 'swap').replace(/_/g, ' ')}</span>
                                {log.buy_usd  && <span className="text-slate-500">buy ${fmt(log.buy_usd)}</span>}
                                {log.sell_usd && <span className="text-slate-500">sell ${fmt(log.sell_usd)}</span>}
                                {log.pnl_usd !== undefined && (
                                  <span className={pnlCls(log.pnl_usd)}>
                                    {parseFloat(log.pnl_usd) >= 0 ? '+' : ''}${fmt(log.pnl_usd, 4)}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 shrink-0 ml-2">
                                {log.output && (
                                  <span className="text-[9px] px-1 rounded bg-slate-700 text-slate-400">{log.output}</span>
                                )}
                                {(log.solscan_buy || log.solscan_sell || log.tx_signature) && (
                                  <div className="flex items-center gap-1">
                                    {log.solscan_buy && (
                                      <a
                                        href={log.solscan_buy}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[9px] text-blue-400 hover:text-blue-300 hover:underline flex items-center gap-0.5"
                                        title="View buy transaction on Solscan"
                                      >
                                        <ExternalLink className="w-2.5 h-2.5" /> Buy
                                      </a>
                                    )}
                                    {log.solscan_sell && (
                                      <a
                                        href={log.solscan_sell}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[9px] text-green-400 hover:text-green-300 hover:underline flex items-center gap-0.5"
                                        title="View sell transaction on Solscan"
                                      >
                                        <ExternalLink className="w-2.5 h-2.5" /> Sell
                                      </a>
                                    )}
                                    {!log.solscan_buy && !log.solscan_sell && log.tx_signature && (
                                      <a
                                        href={`https://solscan.io/tx/${log.tx_signature}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[9px] text-blue-400 hover:text-blue-300 hover:underline flex items-center gap-0.5"
                                        title="View on Solscan"
                                      >
                                        <ExternalLink className="w-2.5 h-2.5" /> Solscan
                                      </a>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
